import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import TableStructure from "../../CommonComponents/TableStructure";
import { getImageURL } from "../../util/getImage";
import { Image, useToast } from "@chakra-ui/react";
import { getAllCalendarEventsData, deleteCalendarEvents } from "../../services/CalendarEvents";
import { handleExport as handleExportUtil } from '../../util/helper';
import { showErrorToast, showSuccessToast } from '../../util/errorHandler';
const IMAGE_BASE_PATH = process.env.REACT_APP_DEV_IMAGE_BASE_PATH;
const placeholderImg = `${IMAGE_BASE_PATH}/ui/DummyImage/small.png`

const CalendarEventsTable = () => {
    const [records, setRecords] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(false);
    const [totalRecords, setTotalRecords] = useState(0);
  const [searchText, setSearchText] = useState('');
    const [sortOrder, setSortOrder] = useState("asc"); // Sorting state
    const session = useSelector((state) => state.session);
    const limit = 50; // Number of records per page
    const toast = useToast();

    // Fetch projects from the API
    const fetchCalendarEvents = async (page, term) => {
        setLoading(true);
        try {
            const result = await getAllCalendarEventsData(limit, page, term);
            if (result?.data) {
                setRecords(citationFactor(result?.data?.data || []));
                setTotalPages(Math.ceil(result?.data?.total / limit));
                setTotalRecords(result?.data?.total);

                if (result?.data?.page_number && parseInt(result.data.page_number) !== currentPage) {
                    setCurrentPage(parseInt(result.data.page_number));
                }
            }
        } catch (error) {
            console.error("Error fetching projects:", error);
        } finally {
            setLoading(false);
        }
    };

    const citationFactor = (data) => {
        return data?.map((item) => {
            return {
                _id: item?._id || item?.id,
                title: item?.title,
                alias: item?.alias,
                eventStatus: item?.eventStatus ? item?.eventStatus.charAt(0).toUpperCase() + item?.eventStatus.slice(1) : "",
                // Preserve the raw boolean so TableStructure can render Yes / No / N/A correctly.
                // Avoid the `||` shortcut here — `false || undefined` would discard the `false` state.
                isVisible: item?.isVisible !== undefined ? item.isVisible : undefined,
                eventType: item?.eventType ? item?.eventType.charAt(0).toUpperCase() + item?.eventType.slice(1) : "",
                startDate: item?.startDate,
                endDate: item?.endDate,
                direct_url_of_cover_image: item?.eventImage,
                projectNumber: item?.projectNumber
            }
        })
    }

    const columns = [
        {
            label: "Project Number",
            accessor: "projectNumber",
        },
        {
            label: "Title",
            accessor: "title",
        },
        {
            label: "Status",
            accessor: "eventStatus",
        },
        {
            label: "Is Visible",
            accessor: "isVisible",
        },
        {
            label: "Event Type",
            accessor: "eventType",
        },
        {
            label: "Event Start Date",
            accessor: "startDate",
        },
        {
            label: "Event End Date",
            accessor: "endDate",
        },
        {
            label: "Image",
            accessor: "direct_url_of_cover_image",
            render: (value) =>
                <Image
                    src={getImageURL(value, "200", "100")}
                    width="200px"
                    height="100px"
                    objectFit="contain"
                    fallbackSrc={placeholderImg}
                    alt="Cover image"
                />
        }
    ]

    // Handle search button click
    const handleSearch = (searchTerm) => {
        if (searchTerm.trim()) {
            isInitialLoad.current = false; // Skip the first render
            setCurrentPage(1); // Reset to page 1 for a new search
            setSearchText(searchTerm);
      fetchCalendarEvents(1, searchTerm);
        }
    };

    const sortRecords = () => {
        const newSortOrder = sortOrder === "asc" ? "desc" : "asc"; // Update sortOrder first
        setSortOrder(newSortOrder);

        const sorted = [...records].sort((a, b) =>
            newSortOrder === "asc"
                ? a.title.localeCompare(b.title)
                : b.title.localeCompare(a.title)
        );

        setRecords(sorted);
    };

    const isInitialLoad = useRef(true);

    useEffect(() => {
        // if (isInitialLoad.current) {
        //     isInitialLoad.current = false; // Skip the first render
        //     return;
        // }
        fetchCalendarEvents(currentPage, searchText);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage]);

    const handleClear = () => {
        setSearchText('');
    fetchCalendarEvents(1, "");
    };

    // Handle page changes
    const handlePageChange = (page) => {
        if (page > 0 && page <= totalPages) {
            setCurrentPage(parseInt(page));
        }
    };

    const getRowKey = (record) => record?._id;

    const handleRowSelect = (key, checked) => {
        setSelectedIds((prev) => {
            if (checked) {
                if (prev.includes(key)) return prev;
                return [...prev, key];
            } else {
                return prev.filter((k) => k !== key);
            }
        });
    };

    const handleSelectAll = (checked) => {
        if (checked) {
            const allKeys = records.map((r) => r?._id).filter(Boolean);
            setSelectedIds(allKeys);
        } else {
            setSelectedIds([]);
        }
    };

    const performDelete = async (idsToDelete) => {
        if (!idsToDelete || idsToDelete.length === 0) return;
        const confirmed = window.confirm(`Delete ${idsToDelete.length} record(s)?`);
        if (!confirmed) return;
        setLoading(true);
        try {
            await deleteCalendarEvents(idsToDelete);
            // After delete, refresh current page and clear selection
            setSelectedIds([]);
            await fetchCalendarEvents(currentPage, searchText);
            showSuccessToast(toast, `Deleted ${idsToDelete.length} record(s)`);
        } catch (error) {
            console.error("Error deleting calendar events:", error);
            showErrorToast(toast, "Error deleting record(s)");
        } finally {
            setLoading(false);
        }
    };

    const handleRowDelete = (key) => {
        performDelete([key]);
    };

    const handleDeleteSelected = () => {
        performDelete(selectedIds);
    };

    const handleExport = () => handleExportUtil("calendar", toast);

    return (
        <TableStructure
            isAuthenticated={session?.isAuthenticated}
            handleSearch={handleSearch}
            handleClear={handleClear}
            handlePageChange={handlePageChange}
            currentPage={currentPage}
            totalPages={totalPages}
            sortRecords={sortRecords}
            sortOrder={sortOrder}
            loading={loading}
            records={records}
            totalRecords={totalRecords}
            type="calendar/events"
            title="Calendar Events"
            addOption
            columns={columns}
            selectable
            selectedRowKeys={selectedIds}
            getRowKey={getRowKey}
            onRowSelect={handleRowSelect}
            onSelectAll={handleSelectAll}
            onRowDelete={handleRowDelete}
            onDeleteSelected={handleDeleteSelected}
            exportHandler={handleExport}
        />
    );
};

export default CalendarEventsTable;
